import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { formatPrice, resolveUrl, resolveEffectivePrice } from "../webOrderApi";

// 2-column grid product tile used on the /order page's product listing.
export default function MenuProductCard({
  product,
  cartQuantity,
  onAdd,
  onSelectProduct,
  onUpdateQuantity,
  onUpdateGroupQuantity,
  onDecrementVariant,
}) {
  const [imageBroken, setImageBroken] = useState(false);
  const hasVariants = (product.variants || []).length > 0;
  const isIndependentMode = product.independentVariants === true;
  // A combination-mode variant product (real Size/Flavor-style variants, not
  // independent attributes) can have several different cart lines at once —
  // "-" reduces whichever one happens to come first rather than picking
  // between them, and "+" always reopens the picker instead of guessing
  // which line to add to. No-variant and independent-mode products have no
  // such ambiguity, so their stepper adjusts the (one, shared) quantity
  // directly. See WebOrderPage's decrementProductVariant.
  const isCombinationVariant = hasVariants && !isIndependentMode;
  const outOfStock = product.outOfStock === true;
  const baseOriginalPrice = Number(product.price);
  const baseEffectivePrice = resolveEffectivePrice(product.price, product.salePrice);
  const useMainPrice = product.useMainProductPrice === true;
  const activeVariants = (product.variants || []).filter((v) => v.status !== "Inactive");
  const variantPricing = activeVariants.map((v) => ({
    original: Number(v.price) > 0 ? Number(v.price) : baseOriginalPrice,
    effective: Number(v.price) > 0 ? resolveEffectivePrice(v.price, v.salePrice) : baseEffectivePrice,
  }));
  const cheapest = useMainPrice
    ? { original: baseOriginalPrice, effective: baseEffectivePrice }
    : variantPricing.length > 0
      ? variantPricing.reduce((min, p) => (p.effective < min.effective ? p : min))
      : { original: baseOriginalPrice, effective: baseEffectivePrice };
  const originalPrice = cheapest.original;
  const effectivePrice = cheapest.effective;
  const onSale = effectivePrice < originalPrice;
  // Listing-only fallback (never used in the product popup, which still
  // skips its image section entirely for a product with none) — a plain
  // asset URL/path, same convention as REACT_APP_STORE_LOGO_URL, not run
  // through resolveUrl since it isn't a backend-relative upload path.
  const placeholderImage = process.env.REACT_APP_PRODUCT_PLACEHOLDER_IMAGE_URL || null;
  const productImage = !imageBroken ? resolveUrl(product.images?.[0]) : null;
  const image = productImage || placeholderImage;

  const handleTap = () => onSelectProduct(product);
  const handleAddClick = (e) => {
    e.stopPropagation();
    if (hasVariants) onSelectProduct(product);
    else onAdd(product, null);
  };
  const handleMinus = (e) => {
    e.stopPropagation();
    if (isCombinationVariant) onDecrementVariant(product.id);
    else if (isIndependentMode) onUpdateGroupQuantity(product.id, cartQuantity - 1);
    else onUpdateQuantity(product, null, cartQuantity - 1);
  };
  const handlePlus = (e) => {
    e.stopPropagation();
    if (isCombinationVariant) onSelectProduct(product);
    else if (isIndependentMode) onUpdateGroupQuantity(product.id, cartQuantity + 1);
    else onUpdateQuantity(product, null, cartQuantity + 1);
  };

  return (
    <div
      onClick={handleTap}
      className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white cursor-pointer group"
    >
      {/* Square image slot — always reserved at the same size whether or not
          this product has a photo, so cards line up evenly across a row
          regardless of which ones do. No photo falls back to the seller's
          own placeholder image if one's configured (see placeholderImage
          above), else leaves it a blank white box. */}
      <div className="relative w-full aspect-square overflow-hidden bg-white border-b border-slate-100">
        {image && (
          <img
            src={image}
            alt={product.name}
            onError={() => setImageBroken(true)}
            className={`h-full w-full object-cover ${outOfStock ? "opacity-50" : ""}`}
          />
        )}
        {/* Static veg mark — every product is veg on this storefront, per the
            seller's request (no per-product veg/non-veg field exists). */}
        <span className="absolute left-2 top-2 flex h-5 w-5 items-center justify-center rounded-[4px] border-2 border-emerald-600 bg-white shadow-xs">
          <span className="h-2 w-2 rounded-full bg-emerald-600" />
        </span>
        {outOfStock && (
          <span className="absolute right-2 top-2 rounded-md bg-white/95 px-1.5 py-0.5 text-[9px] font-bold uppercase text-yellow-600 shadow-xs">
            Out of stock
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1 p-2.5">
        <h3 className="line-clamp-2 text-xs sm:text-sm font-bold leading-snug text-slate-900">{product.name}</h3>
        <div className="flex items-baseline gap-1.5 text-sm font-extrabold text-[#520400]">
          <span>{formatPrice(effectivePrice)}</span>
          {onSale && <span className="text-[10px] font-semibold text-slate-400 line-through">{formatPrice(originalPrice)}</span>}
        </div>

        {outOfStock ? (
          <button
            type="button"
            disabled
            className="mt-auto flex h-9 w-full items-center justify-center rounded-lg bg-slate-200 text-[11px] sm:text-xs font-extrabold text-slate-400 cursor-not-allowed"
          >
            Out of Stock
          </button>
        ) : cartQuantity > 0 ? (
          <div className="mt-auto flex h-9 items-center justify-between rounded-lg bg-[#520400] px-1.5">
            <button
              type="button"
              onClick={handleMinus}
              className="flex h-6 w-6 items-center justify-center rounded-md bg-white/20 hover:bg-white/30 text-white cursor-pointer"
            >
              <Minus className="h-3.5 w-3.5 stroke-[3]" />
            </button>
            <span className="text-sm font-black text-white">{cartQuantity}</span>
            <button
              type="button"
              onClick={handlePlus}
              className="flex h-6 w-6 items-center justify-center rounded-md bg-white/20 hover:bg-white/30 text-white cursor-pointer"
            >
              <Plus className="h-3.5 w-3.5 stroke-[3]" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleAddClick}
            className="mt-auto flex h-9 w-full items-center justify-center rounded-lg bg-[#520400] hover:bg-[#3A0300] text-[11px] sm:text-xs font-extrabold text-white transition-colors"
          >
            + Add
          </button>
        )}
      </div>
    </div>
  );
}
